export interface PrintLayoutConfig {
  costsCount: number
  paymentsCount: number
  vehiclesCount: number
  hasDiscount: boolean
  textLength: number
}

/**
 * Calculates a global scaling factor for V2 documents to fit them on a single A4 page.
 * It uses a pixel-based height estimation model to determine if the document will overflow.
 * If it fits naturally, it remains at 100%.
 * If it slightly overflows, it shrinks the whole document (up to 85%) to fit on one page.
 * If it overflows more than that, it leaves the scale at 85% and allows the browser's
 * native print engine to flow the text naturally onto the next page.
 */
export const calculatePrintLayout = (config: PrintLayoutConfig) => {
  // 1. Estimate Tables Section Height (Page 1 content if split)
  // Base headers, client info, organization info, vehicle info take ~600px
  let tablesHeightPx = 600

  // Add dynamic table heights
  if (config.costsCount > 0) {
    tablesHeightPx += 50 + config.costsCount * 30
  }

  if (config.paymentsCount > 0) {
    tablesHeightPx += 50 + config.paymentsCount * 30
  }

  if (config.vehiclesCount > 0) {
    tablesHeightPx += 50 + config.vehiclesCount * 40
  }

  if (config.hasDiscount) {
    tablesHeightPx += 80
  }

  // 2. Estimate Notes Section Height (Page 2 content if split)
  // Base height for "Observaciones" header, Footer separator, and Signatures is ~120px
  let notesHeightPx = 120

  // At standard 100% scale (16px base font), an A4 portrait line holds ~95 characters.
  const charsPerLine = 95
  const pixelsPerLine = 22

  const basicTextLines = Math.ceil(config.textLength / charsPerLine)
  const paragraphBreaks = Math.floor(config.textLength / 300)

  notesHeightPx += basicTextLines * pixelsPerLine + paragraphBreaks * 15

  // 3. Calculate Totals and Thresholds
  const totalHeightPx = tablesHeightPx + notesHeightPx
  const A4_PRINTABLE_HEIGHT_PX = 1000 // Safe printable vertical pixels on A4

  let scaleFactor = 1.0
  const pageBreak = false // Disabled: let the browser natively flow text across pages

  if (totalHeightPx <= A4_PRINTABLE_HEIGHT_PX) {
    // Scenario A: Everything fits perfectly on 1 page at 100% size.
    scaleFactor = 1.0
  } else {
    // Scenario B: Document overflows. Can we shrink it slightly to fit?
    const fitScale = A4_PRINTABLE_HEIGHT_PX / totalHeightPx

    // We allow shrinking down to 85% to try to keep it on one page.
    // Beyond that, we let it naturally span multiple pages so it remains readable.
    if (fitScale >= 0.85) {
      scaleFactor = fitScale
    } else {
      scaleFactor = 0.85 // Keep it readable, let the browser paginate
    }
  }

  // Calculate the final root font size for the print stylesheet
  const rootFontSizePx = (16 * scaleFactor).toFixed(2)

  return {
    scaleFactor,
    rootFontSizePx,
    pageBreak,
    _debug: { totalHeightPx, tablesHeightPx, notesHeightPx, A4_PRINTABLE_HEIGHT_PX },
  }
}
